import React, { useState, useEffect, useRef } from "react";
import "./AddNewUser.scss";
import { PATCH_EDIT_USER } from "../../utils/api";
import Loader from "../../utils/loader/loader";
import ToastMessage from "../../utils/toasts/toastMessage";
import axios from "axios";
import { getCSVUploadedData } from "../../Redux/Admin_Panel/people/people.action";
import { useDispatch } from "react-redux";
import {
  CLASS_OPTIONS,
  DIVISION_OPTIONS,
  SUBJECT_OPTIONS,
} from "../../utils/subjectBoard";
import { apiRequest } from "../../api/apiRequest";

// ── SubjectMultiSelect ────────────────────────────────────────────────────────
const SubjectMultiSelect = ({ value = [], onChange, hasError }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (subject) => {
    if (value.includes(subject)) {
      onChange(value.filter((s) => s !== subject));
    } else {
      onChange([...value, subject]);
    }
  };

  return (
    <div className="multi-select-wrapper" ref={wrapperRef}>
      <div
        className={`multi-select-trigger ${hasError ? "input-error" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {value.length === 0 ? (
          <span className="multi-select-placeholder">select subject(s)</span>
        ) : (
          <span className="multi-select-value">{value.join(", ")}</span>
        )}
        <span className="multi-select-arrow">{open ? "▲" : "▾"}</span>
      </div>

      {open && (
        <div className="multi-select-dropdown">
          {SUBJECT_OPTIONS.map((subject) => (
            <label key={subject} className="multi-select-option">
              <input
                type="checkbox"
                checked={value.includes(subject)}
                onChange={() => toggle(subject)}
              />
              <span>{subject}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── EditUser ──────────────────────────────────────────────────────────────────
const EditUser = ({ onCancel, onCreate, userData }) => {
  const dispatch = useDispatch();
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    classes: [],
    schoolName: "",
    password: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (userData) {
      setFormData({
        email: userData.email || "",
        firstName: userData.firstname || "",
        lastName: userData.lastname || "",
        schoolName: userData.school,
        classes:
          userData?.classes?.map((c) => ({
            grade: c?.grade || "",
            division: c?.division || "",
            // subject may come as an array OR a comma-separated string from API → normalize to array
            subject: Array.isArray(c?.subject)
              ? c.subject
              : c?.subject
                ? c.subject.split(", ").filter(Boolean)
                : [],
          })) || [],
        newPassword: "",
      });
    }
  }, [userData]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    const isTeacher = userData?.role_id === 2;

    if (!formData.firstName) {
      newErrors.firstName = "First name is required.";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName.trim())) {
      newErrors.firstName =
        "First name must not contain digits or special characters.";
    }

    if (!formData.lastName) {
      newErrors.lastName = "Last name is required.";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName.trim())) {
      newErrors.lastName =
        "Last name must not contain digits or special characters.";
    }

    if (!formData.schoolName) {
      newErrors.schoolName = "School name is required.";
    }

    if (formData.newPassword) {
      if (formData.newPassword.length < 6) {
        newErrors.newPassword = "Password must be at least 6 characters.";
      }
    }

    if (!formData.classes || formData.classes.length === 0) {
      newErrors.classes = "At least one class is required.";
    } else {
      formData.classes.forEach((c, index) => {
        if (!c.grade) {
          newErrors[`grade_${index}`] = "Class is required.";
        }

        if (!c.division) {
          newErrors[`division_${index}`] = "Division is required.";
        }

        // subject is now an array — check length for teachers
        if (isTeacher && (!c.subject || c.subject.length === 0)) {
          newErrors[`subject_${index}`] = "Subject is required.";
        }
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  // const handleUpdateUser = async () => {
  //   const validationErrors = validate();

  //   if (Object.keys(validationErrors).length > 0) {
  //     if (validationErrors.classes) {
  //       setToastState({
  //         show: true,
  //         message: validationErrors.classes,
  //         type: "error",
  //       });
  //     }

  //     setLoading(false);
  //     return;
  //   }

  //   setLoading(true);

  //   try {
  //     const token = localStorage.getItem("access_token") || null;

  //     const headers = {
  //       Authorization: `Bearer ${token}`,
  //     };

  //     const isTeacher = userData?.role_id === 2;

  //     const invalidClass = formData.classes.find((c) => {
  //       const gradeMissing = !c.grade;
  //       const divisionMissing = !c.division;
  //       // subject is now an array
  //       const subjectMissing =
  //         isTeacher && (!c.subject || c.subject.length === 0);
  //       return gradeMissing || divisionMissing || subjectMissing;
  //     });

  //     if (invalidClass) {
  //       setLoading(false);
  //       setToastState({
  //         show: true,
  //         message: isTeacher
  //           ? "Class, Division and Subject are required for all rows."
  //           : "Class and Division are required for all rows.",
  //         type: "error",
  //       });
  //       return;
  //     }

  //     const normalizeSubjects = (subject) => {
  //       const raw = Array.isArray(subject) ? subject : subject ? [subject] : [];
  //       return raw
  //         .flatMap((s) => String(s ?? "").split(","))
  //         .map((s) => s.trim())
  //         .filter(Boolean);
  //     };

  //     const formatClassPayload = (classes, lowerCase = false) => {
  //       return classes.map((c) => {
  //         const cleanSubjects = normalizeSubjects(c.subject); // ← force-clean here
  //         return {
  //           grade: lowerCase ? c.grade.toLowerCase() : c.grade,
  //           division: lowerCase ? c.division.toLowerCase() : c.division,
  //           ...(isTeacher && {
  //             subject: lowerCase
  //               ? cleanSubjects.map((s) => s.toLowerCase())
  //               : cleanSubjects,
  //           }),
  //         };
  //       });
  //     };
  //     const payload = {};

  //     payload.email = formData.email;

  //     if (
  //       formData.firstName?.toLowerCase() !== userData?.firstname?.toLowerCase()
  //     ) {
  //       payload.first_name = formData.firstName;
  //     }

  //     if (
  //       formData.lastName?.toLowerCase() !== userData?.lastname?.toLowerCase()
  //     ) {
  //       payload.last_name = formData.lastName;
  //     }

  //     if (
  //       formData.schoolName?.toLowerCase() !== userData?.school?.toLowerCase()
  //     ) {
  //       payload.school = formData.schoolName;
  //     }

  //     payload.classes = formatClassPayload(formData.classes, false);

  //     if (formData.newPassword) {
  //       payload.new_password = formData.newPassword;
  //     }

  //     const payloadKeys = Object.keys(payload).filter((k) => k !== "email");

  //     if (payloadKeys.length === 0) {
  //       setToastState({
  //         show: true,
  //         message: "No changes detected.",
  //         type: "error",
  //       });
  //       setLoading(false);
  //       return;
  //     }

  //     const resp = await axios.patch(PATCH_EDIT_USER, payload, {
  //       headers,
  //       withCredentials: true,
  //     });

  //     if (resp.status === 200) {
  //       setToastState({
  //         show: true,
  //         message: "User updated successfully!",
  //         type: "success",
  //       });

  //       dispatch(getCSVUploadedData());

  //       setTimeout(() => {
  //         onCancel();
  //       }, 500);
  //     }
  //   } catch (err) {
  //     const detail = err?.response?.data?.detail;

  //     let message = "Error on updating!";
  //     if (typeof detail === "string") {
  //       message = detail;
  //     } else if (Array.isArray(detail)) {
  //       // FastAPI validation errors: [{ type, loc, msg, input }, ...]
  //       message =
  //         detail
  //           .map((d) => d?.msg)
  //           .filter(Boolean)
  //           .join(" | ") || message;
  //     }

  //     setToastState({
  //       show: true,
  //       message,
  //       type: "error",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleUpdateUser = async () => {
  const validationErrors = validate();

  if (Object.keys(validationErrors).length > 0) {
    if (validationErrors.classes) {
      setToastState({
        show: true,
        message: validationErrors.classes,
        type: "error",
      });
    }

    setLoading(false);
    return;
  }

  setLoading(true);

  const isTeacher = userData?.role_id === 2;

  const invalidClass = formData.classes.find((c) => {
    const gradeMissing = !c.grade;
    const divisionMissing = !c.division;
    // subject is now an array
    const subjectMissing =
      isTeacher && (!c.subject || c.subject.length === 0);
    return gradeMissing || divisionMissing || subjectMissing;
  });

  if (invalidClass) {
    setLoading(false);
    setToastState({
      show: true,
      message: isTeacher
        ? "Class, Division and Subject are required for all rows."
        : "Class and Division are required for all rows.",
      type: "error",
    });
    return;
  }

  const normalizeSubjects = (subject) => {
    const raw = Array.isArray(subject) ? subject : subject ? [subject] : [];
    return raw
      .flatMap((s) => String(s ?? "").split(","))
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const formatClassPayload = (classes, lowerCase = false) => {
    return classes.map((c) => {
      const cleanSubjects = normalizeSubjects(c.subject); // ← force-clean here
      return {
        grade: lowerCase ? c.grade.toLowerCase() : c.grade,
        division: lowerCase ? c.division.toLowerCase() : c.division,
        ...(isTeacher && {
          subject: lowerCase
            ? cleanSubjects.map((s) => s.toLowerCase())
            : cleanSubjects,
        }),
      };
    });
  };

  const payload = {};

  payload.email = formData.email;

  if (
    formData.firstName?.toLowerCase() !== userData?.firstname?.toLowerCase()
  ) {
    payload.first_name = formData.firstName;
  }

  if (
    formData.lastName?.toLowerCase() !== userData?.lastname?.toLowerCase()
  ) {
    payload.last_name = formData.lastName;
  }

  if (
    formData.schoolName?.toLowerCase() !== userData?.school?.toLowerCase()
  ) {
    payload.school = formData.schoolName;
  }

  payload.classes = formatClassPayload(formData.classes, false);

  if (formData.newPassword) {
    payload.new_password = formData.newPassword;
  }

  const payloadKeys = Object.keys(payload).filter((k) => k !== "email");

  if (payloadKeys.length === 0) {
    setToastState({
      show: true,
      message: "No changes detected.",
      type: "error",
    });
    setLoading(false);
    return;
  }

  const result = await apiRequest("patch", PATCH_EDIT_USER, payload);

  setLoading(false);

  if (!result.success) {
    setToastState({
      show: true,
      message: result.message || "Error on updating!",
      type: "error",
    });
    return;
  }

  setToastState({
    show: true,
    message: "User updated successfully!",
    type: "success",
  });

  dispatch(getCSVUploadedData());

  setTimeout(() => {
    onCancel();
  }, 500);
};

  const handleClassChange = (index, field, value) => {
    const updatedClasses = [...formData.classes];
    updatedClasses[index][field] = value;
    setFormData((prev) => ({ ...prev, classes: updatedClasses }));

    const errorKey = `${field}_${index}`;
    if (errors[errorKey]) setErrors((prev) => ({ ...prev, [errorKey]: "" }));
  };

  return (
    <div className="add-user-overlay">
      <div className="add-user-container">
        <div className="top-nav">
          <button className="back-link" onClick={onCancel}>
            ← Back to People
          </button>
        </div>

        <div className="form-card">
          <div className="form-header">
            <span>{userData ? "Edit User Details" : "General"}</span>
            <span className="toggle-icon">▲</span>
          </div>

          <div className="form-body">
            {/* Email */}
            <div className="form-row">
              <div className="label-part">
                Email <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  disabled
                />
                {!userData && (
                  <div className="checkbox-group">
                    <input type="checkbox" id="gen-pass" />
                    <label htmlFor="gen-pass">
                      Generate password and notify user
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Password Section */}
            {userData ? (
              <div
                className={`form-row ${errors.newPassword ? "has-error" : ""}`}
              >
                <div className="label-part">New Password</div>
                <div className="input-part">
                  <div className="pass-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      className={errors.newPassword ? "input-error" : ""}
                    />
                    <i
                      className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
                      onClick={togglePasswordVisibility}
                      style={{
                        cursor: "pointer",
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    ></i>
                  </div>
                  {errors.newPassword && (
                    <span className="error-text">{errors.newPassword}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="form-row">
                <div className="label-part">
                  Password <span className="help">?</span>
                </div>
                <div className="input-part">
                  <div className="pass-input-container">
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <span className="eye">👁</span>
                  </div>
                  <div className="checkbox-group">
                    <input type="checkbox" id="force-change" />
                    <label htmlFor="force-change">Force Password Change</label>
                  </div>
                </div>
              </div>
            )}

            {/* First Name */}
            <div className={`form-row ${errors.firstName ? "has-error" : ""}`}>
              <div className="label-part">
                First Name <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={errors.firstName ? "input-error" : ""}
                />
                {errors.firstName && (
                  <span className="error-text">{errors.firstName}</span>
                )}
              </div>
            </div>

            {/* Last Name */}
            <div className={`form-row ${errors.lastName ? "has-error" : ""}`}>
              <div className="label-part">
                Last Name <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={errors.lastName ? "input-error" : ""}
                />
                {errors.lastName && (
                  <span className="error-text">{errors.lastName}</span>
                )}
              </div>
            </div>

            {/* School Name */}
            <div className={`form-row ${errors.schoolName ? "has-error" : ""}`}>
              <div className="label-part">
                School Name <span className="req">*</span>
              </div>
              <div className="input-part">
                <input
                  type="text"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleChange}
                  className={errors.schoolName ? "input-error" : ""}
                />
                {errors.schoolName && (
                  <span className="error-text">{errors.schoolName}</span>
                )}
              </div>
            </div>

            {/* Class & Division rows */}
            {(!formData.classes || formData.classes.length === 0) &&
              setFormData((prev) => ({
                ...prev,
                classes: [{ grade: "", division: "", subject: [] }],
              }))}

            {Array.isArray(formData.classes) &&
              formData.classes.map((c, index) => (
                <div
                  className={`form-row ${
                    errors[`grade_${index}`] ||
                    errors[`division_${index}`] ||
                    errors[`subject_${index}`]
                      ? "has-error"
                      : ""
                  }`}
                  key={index}
                >
                  <div className="label-part">
                    {index === 0 && userData?.role_id === 2
                      ? "Class & Subject"
                      : userData?.role_id === 1
                        ? "Class & Division"
                        : ""}{" "}
                    <span className="req">*</span>
                  </div>

                  <div className="input-part">
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* ── Grade dropdown ── */}
                      <div className="sub-input-wrap">
                        <div className="select-wrapper">
                          <select
                            value={c?.grade}
                            onChange={(e) =>
                              handleClassChange(index, "grade", e.target.value)
                            }
                            className={
                              errors[`grade_${index}`] ? "input-error" : ""
                            }
                          >
                            <option value="">Class</option>
                            {CLASS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <span className="select-arrow">▾</span>
                        </div>
                        {errors[`grade_${index}`] && (
                          <span className="error-text">
                            {errors[`grade_${index}`]}
                          </span>
                        )}
                      </div>

                      {/* ── Division dropdown ── */}
                      <div className="sub-input-wrap">
                        <div className="select-wrapper">
                          <select
                            value={c?.division}
                            onChange={(e) =>
                              handleClassChange(
                                index,
                                "division",
                                e.target.value,
                              )
                            }
                            className={
                              errors[`division_${index}`] ? "input-error" : ""
                            }
                          >
                            <option value="">Division</option>
                            {DIVISION_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <span className="select-arrow">▾</span>
                        </div>
                        {errors[`division_${index}`] && (
                          <span className="error-text">
                            {errors[`division_${index}`]}
                          </span>
                        )}
                      </div>

                      {/* ── Subject multi-select (teacher only) ── */}
                      {userData?.role_id === 2 && (
                        <div className="sub-input-wrap">
                          <SubjectMultiSelect
                            value={Array.isArray(c?.subject) ? c.subject : []}
                            onChange={(newVal) =>
                              handleClassChange(index, "subject", newVal)
                            }
                            hasError={!!errors[`subject_${index}`]}
                          />
                          {errors[`subject_${index}`] && (
                            <span className="error-text">
                              {errors[`subject_${index}`]}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Remove button per row */}
                      {formData.classes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...formData.classes];
                            updated.splice(index, 1);
                            setFormData((prev) => ({
                              ...prev,
                              classes: updated,
                            }));
                          }}
                          className="class-btn remove-btn"
                        >
                          −
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

            {/* Single Add button at the bottom (teacher only) */}
            {userData?.role_id === 2 && (
              <div className="form-row">
                <div className="label-part" />
                <div
                  className="input-part"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        classes: [
                          ...prev.classes,
                          { grade: "", division: "", subject: [] },
                        ],
                      }))
                    }
                    className="class-btn add-btn"
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="form-actions">
              <button className="btn-create" onClick={handleUpdateUser}>
                Update User
              </button>
              <button className="btn-cancel" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Loader show={loading} />
      <ToastMessage
        show={toastState.show}
        onClose={() => setToastState({ show: false, message: "", type: "" })}
        message={toastState.message}
        type={toastState.type}
      />
    </div>
  );
};

export default EditUser;
